import Oruga from '@oruga-ui/oruga-next'
import { bulmaConfig } from '@oruga-ui/theme-bulma'
import { VueQueryPlugin } from '@tanstack/vue-query'
import { MEILI_SEARCH_CONFIG_KEY } from '@thyme/libs/src/meiliSearch/config'
import { STRAPI_CONFIG_KEY } from '@thyme/libs/src/strapi/config'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import PrimeVue from 'primevue/config'
import Tooltip from 'primevue/tooltip'
import { createApp } from 'vue'

// register rules
import { initializeDatadog } from '@/datadog'
import { useConfigStore } from '@/legacy/store/modules/config'
import { initializeEventService } from '@/libs/events/events'
import { QUERY_CLIENT_CONFIG } from '@/libs/query/defaults'
import '@/libs/validation/veeValidate'
import router from '@/router'
import { applyConsolePatches } from '@/utils/console'
import App from './App/App.vue'

applyConsolePatches()

import '@/scss/app.scss' // thymebox scss
// styles for vue-pdf-embed
import 'vue-pdf-embed/dist/styles/annotationLayer.css'
import 'vue-pdf-embed/dist/styles/textLayer.css'
import { auth0 } from './auth'

const app = createApp({
  ...App,
})

const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)
app.use(pinia)
app.use(Oruga, bulmaConfig)
app.use(PrimeVue)
app.use(VueQueryPlugin, {
  enableDevtoolsV6Plugin: true,
  queryClientConfig: QUERY_CLIENT_CONFIG,
})

app.directive('tooltip', Tooltip)

/**
 * v-focus directive
 *
 * Focuses an input as soon as it is added to the page.
 */
app.directive('focus', {
  mounted: (el) => {
    if (el.nodeName === 'LABEL') {
      // focus the label's control, if there is one
      ;(el as HTMLLabelElement).control?.focus()
    } else if (el.nodeName === 'INPUT') {
      // focus the input
      el.focus()
    }

    // otherwise, do nothing
  },
})

/**
 * v-hover directive
 *
 * Use in place of v-if/v-show when an element should continue to take up space
 * on the page even when hidden.
 */
app.directive('visible', {
  mounted(el, binding) {
    el.style.visibility = binding.value ? 'visible' : 'hidden'
  },
  beforeUpdate(el, binding) {
    el.style.visibility = binding.value ? 'visible' : 'hidden'
  },
})

app.directive('click-outside', {
  // bind
  beforeMount(el, binding) {
    el.clickOutsideEvent = function (event: MouseEvent) {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value(event)
      }
    }
    document.body.addEventListener('click', el.clickOutsideEvent)
  },

  // unbind
  unmounted(el) {
    document.body.removeEventListener('click', el.clickOutsideEvent)
  },
})

const configStore = useConfigStore()
configStore.get()

const config = configStore.configVals
initializeDatadog()
initializeEventService({
  eventService: configStore.configVals?.eventService,
  isLocal: configStore.configVals?.env === 'dev',
})

if (config) {
  app.provide(STRAPI_CONFIG_KEY, config.strapi)
  app.provide(MEILI_SEARCH_CONFIG_KEY, config.meiliSearch)
}
app.use(router)
app.use(auth0())
app.mount('#app')
